import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import type {
  PlantillaTipo,
  CreatePlantillaTareaDto,
  UpdatePlantillaTareaDto,
  CreatePlantillaTareaItemDto,
  UpdatePlantillaTareaItemDto,
} from '@/types';

// Lista resumida de plantillas con filtros opcionales.
// Si categoryId es undefined NO filtra por categoria; idem tipo/activo.
// Cuando tipo='checklist' o 'revision' el backend ademas trae las 'ambas'.
export function usePlantillas(filters: {
  categoryId?: number;
  tipo?: PlantillaTipo;
  activo?: boolean;
} = {}) {
  return useQuery({
    queryKey: ['plantillas', filters.categoryId ?? null, filters.tipo ?? null, filters.activo ?? null],
    queryFn: () => api.plantillas.list(filters),
  });
}

// Detalle completo (cabecera + items) de una plantilla.
export function usePlantilla(id: number | null) {
  return useQuery({
    queryKey: ['plantilla', id],
    queryFn: () => api.plantillas.get(id!),
    enabled: id !== null,
  });
}

export function useCreatePlantilla() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreatePlantillaTareaDto) => api.plantillas.create(dto),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['plantillas'] }),
  });
}

export function useUpdatePlantilla() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: UpdatePlantillaTareaDto }) =>
      api.plantillas.update(id, dto),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ['plantillas'] });
      qc.invalidateQueries({ queryKey: ['plantilla', id] });
    },
  });
}

export function useDeletePlantilla() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.plantillas.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['plantillas'] }),
  });
}

export function useDuplicatePlantilla() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.plantillas.duplicate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['plantillas'] }),
  });
}

export function useCreatePlantillaItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ plantillaId, dto }: { plantillaId: number; dto: CreatePlantillaTareaItemDto }) =>
      api.plantillas.items.create(plantillaId, dto),
    onSuccess: (_, { plantillaId }) => {
      qc.invalidateQueries({ queryKey: ['plantilla', plantillaId] });
      qc.invalidateQueries({ queryKey: ['plantillas'] });
    },
  });
}

export function useUpdatePlantillaItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      plantillaId, itemId, dto,
    }: { plantillaId: number; itemId: number; dto: UpdatePlantillaTareaItemDto }) =>
      api.plantillas.items.update(plantillaId, itemId, dto),
    onSuccess: (_, { plantillaId }) => {
      qc.invalidateQueries({ queryKey: ['plantilla', plantillaId] });
    },
  });
}

export function useDeletePlantillaItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ plantillaId, itemId }: { plantillaId: number; itemId: number }) =>
      api.plantillas.items.remove(plantillaId, itemId),
    onSuccess: (_, { plantillaId }) => {
      qc.invalidateQueries({ queryKey: ['plantilla', plantillaId] });
      qc.invalidateQueries({ queryKey: ['plantillas'] });
    },
  });
}

export function useReorderPlantillaItems() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ plantillaId, orderedIds }: { plantillaId: number; orderedIds: number[] }) =>
      api.plantillas.items.reorder(plantillaId, orderedIds),
    onSuccess: (_, { plantillaId }) => {
      qc.invalidateQueries({ queryKey: ['plantilla', plantillaId] });
    },
  });
}
